/* Panneau de filtres de la page collection — port de `CollectionFilterPanel`
 * de marineserre.com (relevé du 1er octobre 2026 ; markup et CSS dans
 * snippets/collection-filters.liquid).
 *
 * Ce que fait l'original : un clic sur une valeur filtre la grille sur-le-champ,
 * le panneau reste ouvert, le bouton « Filtres » de la barre basse affiche le
 * nombre de valeurs actives entre crochets et un bouton « effacer » apparaît à
 * côté ; « Appliquer » ne fait que fermer le panneau ; « effacer » retire tout.
 * Ses filtres sont calculés dans le navigateur et ne touchent jamais l'URL.
 *
 * Ce que fait le thème : la même chose, mais avec les filtres natifs de Shopify
 * (paramètres `filter.*` et `sort_by` de l'URL). Chaque valeur du panneau porte
 * l'URL que Shopify donne pour l'ajouter ou la retirer (`url_to_add` /
 * `url_to_remove`) ; au clic, on redemande la section de la collection à cette
 * URL par l'API de rendu de sections (`section_id`), puis on remplace dans la
 * page les quatre morceaux qui dépendent des filtres : la grille, le bouton
 * « Voir plus », le groupe de boutons de la barre et le contenu du panneau. Le
 * `<dialog>` et ses boutons du bas restent, donc le panneau reste ouvert et le
 * focus revient sur la valeur cliquée. L'URL de la fenêtre suit par
 * `history.replaceState` : la page filtrée se recharge et se partage.
 *
 * Les paramètres imposés (`data-fixed-params`, la disponibilité « en stock ») ne
 * sont pas dans l'URL de la page nue, donc pas dans le rendu initial du panneau :
 * ses compteurs y comptent les produits épuisés, et une taille dont la seule
 * variante est épuisée paraît disponible alors que son filtrage ne rend rien —
 * Shopify exige qu'une même variante satisfasse tous les filtres `filter.v.*`.
 * Au chargement, si l'URL ne porte pas ces paramètres, le composant redemande
 * donc la section avec eux et ne remplace que le contenu du panneau (ni la
 * grille, ni l'URL, ni la barre) : les valeurs sans stock sont grisées avant
 * que le panneau ne s'ouvre.
 *
 * Pourquoi un fichier dans `assets/` et non un bloc `{% javascript %}` : la
 * taille des bundles du thème, que `shopify theme dev` tronque (voir CLAUDE.md).
 * Chargé en `defer` par sections/collection.liquid.
 *
 * Contrat de markup (tout est en `data-*`, le graphisme est libre) :
 *
 *   <div class="collection">                        le conteneur commun
 *     .collection__frame                            la grille, remplacée entière
 *     .collection__more                             le « Voir plus », remplacé,
 *                                                   ajouté ou retiré selon la page
 *     [data-filters-bar]                            le groupe de boutons de la barre,
 *                                                   remplacé entier ; il contient
 *       [data-filters-open]                           ouvre le panneau
 *       [data-filters-clear]                          retire tous les filtres (facultatif)
 *     <collection-filters data-section-id="…"       ce composant
 *                         data-clear-url="…"        l'URL de la collection nue
 *                         data-fixed-params="…">    paramètres posés sur chaque URL
 *                                                   chargée, au format d'une query
 *                                                   string (`filter.v.availability=1` :
 *                                                   seuls les produits en stock, le
 *                                                   filtre lui-même étant caché)
 *                         data-scope-param="…">     le paramètre du filtre de famille
 *                                                   des tailles (facultatif, voir
 *                                                   `scopedUrl`)
 *       <dialog>
 *         [data-filters-status]                     zone `role="status"` : nombre de produits
 *         [data-filters-content data-filters-count="…"]
 *                                                   le contenu remplacé, et le texte
 *                                                   à annoncer après chaque filtrage
 *           [data-filter-url="…" data-filter-key="…" aria-pressed]
 *                                                   une valeur : l'URL à charger, et une
 *                                                   clé stable pour retrouver le focus
 *             [data-filter-scope="…"]               sur une taille : sa famille (valeur
 *                                                   du filtre de famille, vide = aucune) ;
 *                                                   l'URL est alors construite ici
 *           [data-sort="price-ascending" data-filter-key="…" aria-pressed]
 *                                                   un tri : le `sort_by` à poser
 *                                                   (retiré si le bouton est déjà pressé)
 *         [data-filters-close]                      ferme (la croix, « Appliquer »)
 *         [data-filters-clear]                      retire tous les filtres
 *       [data-filters-status-page]                  seconde zone `role="status"`, hors
 *                                                   du dialogue : parle quand il est fermé
 *
 *   La section redemandée doit rendre les mêmes éléments : c'est le même
 *   fichier Liquid, rendu avec les nouveaux paramètres.
 */

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.collection = this.closest('.collection');
    if (!this.dialog || !this.collection) return;

    this.onClick = this.onClick.bind(this);
    this.onCollectionClick = this.onCollectionClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.addEventListener('click', this.onClick);
    /* Les boutons de la barre basse sont hors du composant, dans `.collection`. */
    this.collection.addEventListener('click', this.onCollectionClick);
    this.dialog.addEventListener('close', this.onDialogClose);

    if (this.hasFixedParams(window.location.href)) {
      this.dimFamilies();
    } else {
      this.refresh();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.collection?.removeEventListener('click', this.onCollectionClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.controller?.abort();
    this.countsController?.abort();
    clearTimeout(this.statusTimer);
  }

  /* À la fermeture (croix, « Appliquer », Échap, voile), le navigateur rend
     le focus au bouton qui a ouvert le dialogue — mais ce bouton a été
     remplacé au premier filtrage, et un élément détaché ne peut plus le
     recevoir : il tombait sur <body>, et Tab repartait du haut de la page.
     On vise donc le bouton « Filtres » courant, quel qu'il soit. `close`
     arrive après la restauration native, qu'il remplace. */
  onDialogClose() {
    this.collection.querySelector('[data-filters-open]')?.focus({ preventScroll: true });
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
    /* L'événement `close` arrive en tâche différée (jamais tant que
       l'onglet est masqué) : pour les fermetures qui passent par ici, on
       n'attend pas. Échap passe par l'événement seul. */
    this.onDialogClose();
  }

  /* Les paramètres imposés, au format d'une query string, en paires nom/valeur. */
  get fixedParams() {
    return new URLSearchParams(this.dataset.fixedParams || '');
  }

  /* L'URL porte-t-elle déjà chaque paramètre imposé, avec sa valeur ? */
  hasFixedParams(url) {
    const { searchParams } = new URL(url, window.location.href);
    for (const [name, value] of this.fixedParams) {
      if (searchParams.get(name) !== value) return false;
    }
    return true;
  }

  /* L'URL à charger pour `url` : sans `section_id`, avec les paramètres imposés
     — « effacer » compris, ils ne font pas partie des filtres visibles. */
  targetFor(url) {
    const target = new URL(url, window.location.href);
    target.searchParams.delete('section_id');
    for (const [name, value] of this.fixedParams) target.searchParams.set(name, value);
    return target;
  }

  /* Prépare la requête de la section rendue à `target`. Toute requête
     précédente est abandonnée : seule la dernière compte. Synchrone, pour que
     l'appelant tienne son `signal` avant d'attendre. */
  prepare(target) {
    this.controller?.abort();
    this.controller = new AbortController();

    const request = new URL(target);
    request.searchParams.set('section_id', this.dataset.sectionId);
    return { request, signal: this.controller.signal };
  }

  /* Demande la section à Shopify et la retourne en document. */
  async fetchSection(request, signal) {
    const response = await fetch(request, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  onCollectionClick(event) {
    /* Les clics dans le dialogue sont traités par onClick ; le `<dialog>` est
       un descendant de `.collection`, donc ils remontent aussi jusqu'ici. */
    if (this.contains(event.target)) return;

    if (event.target.closest('[data-filters-open]')) return this.open();

    const clear = event.target.closest('[data-filters-clear]');
    if (clear) return this.apply(this.dataset.clearUrl, { bar: true });
  }

  onClick(event) {
    const { target } = event;

    /* Un clic sur le voile : la cible est le dialogue lui-même, dont le
       padding est nul, donc tout clic « sur lui » est hors du cadre. */
    if (target === this.dialog) return this.close();
    if (target.closest('[data-filters-close]')) return this.close();
    if (target.closest('[data-filters-clear]')) return this.apply(this.dataset.clearUrl);

    const value = target.closest('[data-filter-url]');
    if (value) {
      const focus = { key: value.dataset.filterKey, index: this.indexOf(value) };
      const url = 'filterScope' in value.dataset ? this.scopedUrl(value) : value.dataset.filterUrl;
      return this.apply(url, focus);
    }

    const sort = target.closest('[data-sort]');
    if (sort) {
      /* Shopify ne donne pas d'URL pour le tri : on la construit à partir de
         l'URL courante, qui porte déjà les filtres actifs. Repressé, le tri
         s'enlève ; `page` est retiré, la sélection change de longueur. */
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      if (sort.getAttribute('aria-pressed') === 'true') {
        url.searchParams.delete('sort_by');
      } else {
        url.searchParams.set('sort_by', sort.dataset.sort);
      }
      return this.apply(url.pathname + url.search, { key: sort.dataset.filterKey });
    }
  }

  /* L'URL d'une taille rangée sous une famille (Vêtements, Chaussures…).
     Le filtre de taille de Shopify ignore la famille : un 44 choisi sous
     Chaussures listerait aussi les robes en 44. La taille porte donc la
     valeur du filtre de famille (`data-filter-scope`, paramètre nommé par
     `data-scope-param` sur le composant), et on pose les deux filtres
     ensemble : entre deux filtres, Shopify fait un ET. Une famille à la
     fois, Shopify ne sachant pas écrire « (38 ET chaussures) OU (M ET
     vêtements) » :
       - une taille d'une autre famille remplace les tailles choisies avant ;
       - la dernière taille retirée emporte sa famille ;
       - une famille vide (taille rangée sous aucun titre) retire la famille.
     Shopify ne donnant pas cette URL, on la construit à partir de l'URL
     courante, comme pour le tri. */
  scopedUrl(value) {
    const url = new URL(window.location.href);
    const scopeParam = this.dataset.scopeParam;
    const scope = value.dataset.filterScope;
    const key = value.dataset.filterKey;
    const name = key.slice(0, key.indexOf('='));
    const size = key.slice(key.indexOf('=') + 1);
    const params = url.searchParams;
    url.searchParams.delete('page');

    const sizes = params.getAll(name);
    params.delete(name);

    if (value.getAttribute('aria-pressed') === 'true') {
      /* Retirée : les autres tailles restent, la famille part avec la dernière. */
      const rest = sizes.filter((other) => other !== size);
      rest.forEach((other) => params.append(name, other));
      if (!rest.length) params.delete(scopeParam);
    } else {
      const current = params.getAll(scopeParam);
      const same = scope ? current.length === 1 && current[0] === scope : current.length === 0;
      /* Même famille : on ajoute la taille aux autres ; sinon elle les remplace. */
      if (same) sizes.filter((other) => other !== size).forEach((other) => params.append(name, other));
      params.delete(scopeParam);
      if (scope) params.set(scopeParam, scope);
      params.append(name, size);
    }
    return url.pathname + url.search;
  }

  /* Grise les tailles de chaque famille d'après les compteurs de cette famille.
     Shopify compte une taille sur toute la sélection : dans new-in-women, le
     42 n'était pas grisé sous Chaussures parce que des robes existent en 42,
     alors qu'aucune chaussure ne l'est, et le choisir ne listait rien. On
     demande donc, pour chaque famille du panneau, la petite section
     `collection-size-counts` à l'URL courante, sans taille et avec cette
     famille : elle rend les compteurs des tailles en JSON. Les requêtes
     partent en parallèle après chaque rendu du panneau ; un nouveau rendu
     abandonne celles en cours. Une taille sélectionnée n'est jamais grisée.
     En cas d'échec, le grisage de Shopify reste. */
  async dimFamilies() {
    const scopeParam = this.dataset.scopeParam;
    if (!scopeParam) return;
    const buttons = [...this.querySelectorAll('[data-filter-scope]')].filter((button) => button.dataset.filterScope);
    const families = [...new Set(buttons.map((button) => button.dataset.filterScope))];
    if (!families.length) return;

    this.countsController?.abort();
    const controller = (this.countsController = new AbortController());

    const nameOf = (button) => button.dataset.filterKey.slice(0, button.dataset.filterKey.indexOf('='));
    const valueOf = (button) => button.dataset.filterKey.slice(button.dataset.filterKey.indexOf('=') + 1);
    const base = this.targetFor(window.location.href);
    new Set(buttons.map(nameOf)).forEach((name) => base.searchParams.delete(name));
    ['page', 'sort_by', scopeParam].forEach((name) => base.searchParams.delete(name));
    base.searchParams.set('section_id', 'collection-size-counts');

    try {
      const counts = new Map(
        await Promise.all(
          families.map(async (family) => {
            const request = new URL(base);
            request.searchParams.set(scopeParam, family);
            const response = await fetch(request, { signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            return [family, JSON.parse(html.querySelector('[data-size-counts]')?.textContent || '{}')];
          })
        )
      );
      if (controller.signal.aborted) return;

      buttons.forEach((button) => {
        const count = counts.get(button.dataset.filterScope)?.[valueOf(button)];
        if (count === undefined) return;
        const empty = count === 0 && button.getAttribute('aria-pressed') !== 'true';
        button.classList.toggle('is-inactive', empty);
        /* Le grisage est une couleur : la mention lue par les lecteurs
           d'écran suit, comme dans le rendu Liquid. */
        let note = button.querySelector('[data-value-unavailable]');
        if (empty && !note) {
          note = document.createElement('span');
          note.className = 'visually-hidden';
          note.dataset.valueUnavailable = '';
          note.textContent = ` (${this.dataset.unavailableLabel || ''})`;
          button.append(note);
        } else if (!empty) {
          note?.remove();
        }
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  /* Charge la collection à `url` (chemin + paramètres de filtre) et remplace
     les morceaux de la page qui en dépendent. `focus.key` désigne la valeur à
     refocaliser après le rendu ; `focus.bar`, le bouton d'ouverture de la barre
     (son voisin « effacer » disparaît avec le rendu). */
  async apply(url, focus = {}) {
    const target = this.targetFor(url);
    const { request, signal } = this.prepare(target);
    this.setAttribute('aria-busy', 'true');

    try {
      const count = this.render(await this.fetchSection(request, signal));
      window.history.replaceState(window.history.state, '', target.pathname + target.search);
      this.dimFamilies();

      if (focus.key) {
        this.focusKey(focus.key, focus.index);
      } else if (focus.bar) {
        this.collection.querySelector('[data-filters-open]')?.focus({ preventScroll: true });
      }
      /* L'annonce vient en dernier : après la levée d'`aria-busy` (un
         sous-arbre occupé est muet pour certains lecteurs d'écran) et après
         le déplacement du focus, qui interromprait la lecture en cours. */
      this.removeAttribute('aria-busy');
      this.announce(count);
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* La page filtrée existe toujours : on y va. */
      console.error(error);
      window.location.href = target.href;
      return;
    } finally {
      if (!signal.aborted) this.removeAttribute('aria-busy');
    }
  }

  /* Au chargement d'une page sans les paramètres imposés : redemande la section
     avec eux et ne remplace que le contenu du panneau, pour que ses compteurs
     — donc le grisage des valeurs — tiennent compte du stock. La grille, la
     barre et l'URL restent celles de la page. Le focus, s'il est déjà sur une
     valeur, revient dessus. Un échec laisse le rendu initial, qui reste
     fonctionnel : le premier clic charge la page avec les paramètres. */
  async refresh() {
    const { request, signal } = this.prepare(this.targetFor(window.location.href));
    this.setAttribute('aria-busy', 'true');

    try {
      const html = await this.fetchSection(request, signal);
      const active = document.activeElement?.closest?.('[data-filter-key]');
      const key = active?.dataset.filterKey;
      const index = active ? this.indexOf(active) : 0;
      this.renderPanel(html);
      if (key) this.focusKey(key, index);
      this.dimFamilies();
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      if (!signal.aborted) this.removeAttribute('aria-busy');
    }
  }

  /* Les éléments qui portent la clé `key`, dans l'ordre du panneau. Une
     taille nommée par deux listes (38 sous Vêtements et sous Chaussures, OS
     sous Accessoires et Bijoux) est rendue deux fois avec la même clé : le
     rang distingue les deux. */
  withKey(key) {
    return [...this.querySelectorAll(`[data-filter-key="${CSS.escape(key)}"]`)];
  }

  indexOf(element) {
    return Math.max(0, this.withKey(element.dataset.filterKey).indexOf(element));
  }

  /* Refocalise la valeur de clé `key` (au rang `index` si elle est rendue
     plusieurs fois), sinon — valeur disparue du nouveau rendu — le premier
     bouton du contenu, sinon la croix : jamais <body>. Le contenu étant
     remplacé, ses zones de défilement repartent de zéro : la valeur est
     ramenée dans la vue au plus près, sans sauter. */
  focusKey(key, index = 0) {
    if (!this.dialog.open) return;
    const all = this.withKey(key);
    const target =
      all[index] ||
      all[0] ||
      this.querySelector('[data-filters-content] button') ||
      this.querySelector('[data-filters-close]');
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /* Le nombre de produits, dit après chaque filtrage. La zone du dialogue
     est muette quand il est fermé (« Effacer » de la barre, `display: none`) :
     une seconde zone, hors du dialogue, prend alors le relais — et une seule
     hors du dialogue ne suffirait pas, le dialogue modal rendant le reste de
     la page inerte. Vidée puis remplie, pour qu'un même nombre (un tri, qui
     ne change pas la sélection) soit relu. */
  announce(text) {
    const status = this.dialog.open
      ? this.querySelector('[data-filters-status]')
      : this.querySelector('[data-filters-status-page]');
    if (!status || text == null) return;
    status.textContent = '';
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => {
      status.textContent = text;
    }, 150);
  }

  /* Remplace, depuis le document rendu par Shopify, la grille, le « Voir
     plus », le groupe de la barre et le contenu du panneau. Les nœuds venus de
     l'autre document sont adoptés à l'insertion ; leurs images ne se chargent
     qu'à ce moment. */
  render(html) {
    const swap = this.swapper(html);
    const frame = swap('.collection__frame');

    /* Le « Voir plus » n'existe que s'il reste une page : il peut apparaître
       ou disparaître avec la sélection. Il se place après la grille. */
    const more = swap('.collection__more');
    if (!more.current && more.next) frame.next?.after(more.next);
    if (more.current && !more.next) more.current.remove();

    swap('[data-filters-bar]');
    return this.renderPanel(html);
  }

  /* Remplace le seul contenu du panneau, en conservant la position de ses
     zones de défilement (les colonnes, les blocs empilés, et sous 900px le
     contenu lui-même) : les nœuds neufs repartent de zéro, et la vue
     sauterait en haut à chaque clic. Retourne le texte à annoncer (le nombre
     de produits), que l'appelant dit au bon moment. */
  renderPanel(html) {
    const scrollers = '.collection-filters__group, .collection-filters__stack';
    const old = this.querySelector('[data-filters-content]');
    const positions = old ? [old, ...old.querySelectorAll(scrollers)].map((element) => element.scrollTop) : [];
    const content = this.swapper(html)('[data-filters-content]', this);
    if (content.current && content.next) {
      [content.next, ...content.next.querySelectorAll(scrollers)].forEach((element, i) => {
        if (positions[i]) element.scrollTop = positions[i];
      });
    }
    return content.next ? content.next.dataset.filtersCount || '' : null;
  }

  /* Un remplaceur : l'élément de la page désigné par `selector` (cherché dans
     `root`, `.collection` par défaut) est remplacé par son homologue de `html`. */
  swapper(html) {
    return (selector, root = this.collection) => {
      const current = root.querySelector(selector);
      const next = html.querySelector(selector);
      if (current && next) current.replaceWith(next);
      return { current, next };
    };
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
